import type { components } from "@sagot/api-client";
import { CONVERSATION_PRIORITIES, type ConversationPriority } from "@sagot/constants";
import type { BadgeTone, IconName } from "@sagot/ui";

type Schemas = components["schemas"];
/** A list row or a thread's conversation (priority, SLA clock and rating included). */
export type ConversationItem = Schemas["ConversationItem"];
export type ConversationDetail = Schemas["ConversationDetail"];
export type SlaTarget = Schemas["SlaTargetView"];
export type ConversationPage = Schemas["ConversationPage"];
export type MessageView = Schemas["MessageView"];
export type TimelineView = Schemas["TimelineView"];
export type InboxView = Schemas["InboxView"];
export type ChannelType = Schemas["ChannelType"];
export type ConversationStatus = Schemas["ConversationStatus"];
export type SavedReply = Schemas["SavedReplyOut"];

export const INBOX_VIEWS: readonly InboxView[] = ["all", "needs_staff", "mine", "my_teams", "ai_resolved", "leads", "snoozed", "closed"];
export const FILTER_CHANNELS: readonly ChannelType[] = ["widget", "messenger", "webapp", "mobile", "email", "api"];
export const EDITABLE_STATUSES: readonly ConversationStatus[] = ["open", "waiting", "resolved", "closed"];
export const DATE_RANGES = ["any", "today", "7d", "30d"] as const;
export type DateRange = (typeof DATE_RANGES)[number];
export const INBOX_SORTS = ["recent", "priority"] as const;
export type InboxSort = (typeof INBOX_SORTS)[number];

export const CHANNEL_ICON: Readonly<Record<string, IconName>> = {
  widget: "globe",
  messenger: "message-circle",
  webapp: "app-window",
  mobile: "smartphone",
  email: "mail",
  api: "code",
  playground: "flask",
  slack: "hash",
  teams: "users",
};

export interface InboxFilters {
  view: InboxView;
  q: string;
  channels: readonly ChannelType[];
  agentId: string | null;
  /** Only conversations routed to this team. */
  teamId: string | null;
  tag: string | null;
  range: DateRange;
  /** Only conversations with this priority. */
  priority: ConversationPriority | null;
  /** `priority` puts urgent first, then high, newest first within each. */
  sort: InboxSort;
}

export const DEFAULT_FILTERS: InboxFilters = { view: "all", q: "", channels: [], agentId: null, teamId: null, tag: null, range: "any", priority: null, sort: "recent" };

const DAY_MS = 86_400_000;

function sinceFor(range: DateRange, now: Date): string | null {
  if (range === "any") return null;
  if (range === "today") {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return start.toISOString();
  }
  const days = range === "7d" ? 7 : 30;
  return new Date(now.getTime() - days * DAY_MS).toISOString();
}

/** `GET /v1/conversations` query for the current filters (empty values omitted). */
export function toListQuery(filters: InboxFilters, now = new Date()) {
  const q = filters.q.trim();
  const since = sinceFor(filters.range, now);
  return {
    view: filters.view,
    ...(q ? { q } : {}),
    ...(filters.channels.length ? { channel: [...filters.channels] } : {}),
    ...(filters.agentId ? { agent_id: filters.agentId } : {}),
    ...(filters.teamId ? { team_id: filters.teamId } : {}),
    ...(filters.tag ? { tag: filters.tag } : {}),
    ...(filters.priority ? { priority: filters.priority } : {}),
    ...(filters.sort === "priority" ? { sort: filters.sort } : {}),
    ...(since ? { since } : {}),
  };
}

const priorityRank = (item: Pick<ConversationItem, "priority">): number => CONVERSATION_PRIORITIES.indexOf(item.priority);

/** The list as the filters ask for it: one priority only, and urgent / high first when sorted by priority (stable, so newest stays first). */
export function arrangeByPriority<T extends Pick<ConversationItem, "priority">>(items: readonly T[], filters: Pick<InboxFilters, "priority" | "sort">): T[] {
  const kept = filters.priority ? items.filter((item) => item.priority === filters.priority) : [...items];
  return filters.sort === "priority" ? kept.sort((a, b) => priorityRank(b) - priorityRank(a)) : kept;
}

/** Number of active (non-default) filter-menu fields, shown on the filter button. */
export function activeFilterCount(filters: InboxFilters): number {
  return [filters.channels.length > 0, filters.agentId !== null, filters.teamId !== null, filters.tag !== null, filters.range !== "any", filters.priority !== null, filters.sort !== "recent"].filter(Boolean).length;
}

export interface StatusSpec {
  key: "needsStaff" | ConversationStatus | "lead";
  tone: BadgeTone;
  icon: IconName | null;
}

/** List/status badge: needs-staff wins, then status (icon + text + colour, never colour alone). */
export function statusSpec(item: Pick<ConversationItem, "needs_staff" | "status">): StatusSpec {
  if (item.needs_staff) return { key: "needsStaff", tone: "danger", icon: null };
  switch (item.status) {
    case "resolved":
      return { key: "resolved", tone: "success", icon: null };
    case "waiting":
      return { key: "waiting", tone: "warning", icon: null };
    case "closed":
    case "abandoned":
      return { key: item.status, tone: "neutral", icon: null };
    default:
      return { key: "open", tone: "brand", icon: null };
  }
}

export function isEditableStatus(value: string): value is ConversationStatus {
  return EDITABLE_STATUSES.some((s) => s === value);
}

export function initialsFor(name: string): string | null {
  if (/^visitor\b/i.test(name) || name.trim().startsWith("#")) return null;
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? [parts[0], parts.at(-1)] : [parts[0]];
  const initials = letters.map((p) => p?.[0] ?? "").join("").toUpperCase();
  return initials || null;
}
